<script lang="ts">

  import { Button } from "$lib/components/ui/button"
  import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "$lib/components/ui/command"
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { Popover, PopoverButton, PopoverContent } from "$lib/components/ui/popover"
  import MinusIcon from "@lucide/svelte/icons/minus"
  import PlusIcon from "@lucide/svelte/icons/plus"
  import { createForm } from "@tanstack/svelte-form"
  import { z } from "zod/v4"
  import type { OrderItem } from "./../../_types"

  const FormSchema = z.object({
    id: z.number().default(0),
    ref_id: z.string().default(""),
    product_id: z.number().default(0),
    product_number: z.string().default(""),
    product_name: z.string().default(""),
    quantity: z.number().default(1),
    unit: z.string().default(""),
    is_free: z.boolean().default(false),
    discount_percentage_raw: z.number().default(0),
    price_raw: z.number().default(0),
    discount_percentage: z.number().default(0),
    price: z.number().default(0),
    order: z.number().default(0),
    total: z.number().default(0),
    description: z.string().default(""),
  })
  const FormEmpty = FormSchema.parse({})

  let {
    open,
    setOpen,
    value,
    save,
  }: {
    open: boolean
    setOpen: (value: boolean) => void
    value: OrderItem
    save: (value: OrderItem) => void
  } = $props()
  let wasOpen = false

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))
  const formWatch = form.useSelector((state) => state.values)

  const discounts = $derived.by(() => {

    const max = formWatch.current.discount_percentage_raw || 0
    const result: string[] = []

    if (max <= 0) return result

    for (let amount = max; amount >= 0; amount -= 2.5) {
      result.push(amount.toFixed(2))
    }

    return result
  })

  function recalculate(quantity: number, discount: number, resetMaximum = false) {

    if (!Number.isFinite(quantity)) {
      return
    }

    const priceRaw = form.getFieldValue("price_raw")
    const price = priceRaw - priceRaw * (discount / 100)
    const normalizedQuantity = Math.max(1, quantity)

    form.setFieldValue("quantity", normalizedQuantity)
    if (resetMaximum) form.setFieldValue("discount_percentage_raw", 0)
    form.setFieldValue("discount_percentage", discount)
    form.setFieldValue("price", price)
    form.setFieldValue("total", price * normalizedQuantity)
  }

  $effect(() => {

    if (open && !wasOpen) {
      form.reset({ ...FormEmpty, ...value, quantity: value.quantity || 1 })
    }

    wasOpen = open
  })

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader class="hidden">
      <DialogTitle>Item</DialogTitle>
      <DialogDescription>Atur jumlah dan diskon item</DialogDescription>
    </DialogHeader>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-6"
      onsubmit={(event) => {
        event.preventDefault()
        save(form.state.values)
        setOpen(false)
      }}>
      <div class="space-y-3">
        <div>
          <FieldLabel>Nomor</FieldLabel>
          <div class="text-desc text-sm font-mono">{formWatch.current.product_number || "-"}</div>
        </div>
        <div>
          <FieldLabel>Nama</FieldLabel>
          <div class="text-desc text-sm">{formWatch.current.product_name || "-"}</div>
        </div>
        <div>
          <FieldLabel>Keterangan</FieldLabel>
          <div class="text-desc text-sm">{formWatch.current.description || "-"}</div>
        </div>
      </div>
      <form.Field name="quantity">
        {#snippet children(field)}
          <div>
            <div class="flex space-x-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onclick={() => recalculate(field.state.value - 1, 0, true)}>
                <MinusIcon aria-hidden="true"/>
              </Button>
              <Input
                type="text"
                name={field.name}
                class="flex-1 text-center font-mono"
                value={field.state.value.toNumeric()}
                oninput={(event) => {
                  recalculate(event.currentTarget.value.toNumber(), 0, true)
                }}/>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onclick={() => recalculate(field.state.value + 1, 0, true)}>
                <PlusIcon aria-hidden="true"/>
              </Button>
            </div>
            <FieldMessage
              name={field.name}
              form={form}/>
          </div>
        {/snippet}
      </form.Field>
      <div class="flex space-x-3">
        <div class="flex-1">
          <FieldLabel>Jumlah</FieldLabel>
          <div class="text-desc text-sm font-mono">
            {formWatch.current.quantity.toLocaleString("en-US")}
          </div>
        </div>
        <div class="flex-1">
          <FieldLabel>Satuan</FieldLabel>
          <div class="text-desc text-sm">{formWatch.current.unit || "-"}</div>
        </div>
      </div>
      <div class="flex space-x-3">
        <div class="flex-1">
          <FieldLabel>Harga</FieldLabel>
          <Input
            type="text"
            name="price"
            value={formWatch.current.price_raw.toLocaleString("en-US")}
            readonly
            class="font-mono"/>
        </div>
        <div class="flex-1">
          <FieldLabel>Diskon (%)</FieldLabel>
          <Popover>
            <PopoverButton
              class="w-full min-w-0"
              value={formWatch.current.discount_percentage.toFixed(2)}/>
            <PopoverContent>
              <Command shouldFilter={false}>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each discounts as option (option)}
                      <CommandItem
                        value={option}
                        onclick={() => {
                          recalculate(form.getFieldValue("quantity"), Number(option))
                          document.keyEsc()
                        }}>
                        {option}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div class="flex space-x-3">
        <div class="flex-1">
          <FieldLabel>Harga</FieldLabel>
          <div class="text-desc text-sm font-mono">
            {formWatch.current.price.toLocaleString("en-US")}
          </div>
        </div>
        <div class="flex-1">
          <FieldLabel>Total</FieldLabel>
          <div class="text-desc text-sm font-mono">
            {formWatch.current.total.toLocaleString("en-US")}
          </div>
        </div>
      </div>
      <DialogFooter>
        <div class="flex-1"></div>
        <Button
          type="button"
          variant="outline"
          onclick={() => setOpen(false)}>
          Batal
        </Button>
        <Button type="submit">Simpan</Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
